"use client";

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Image as ImageIcon, MapPin, Layers, ScanLine, Leaf, History } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

// --- Hooks ---

type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;
  useIsomorphicLayoutEffect(() => {
    const node = ref.current;
    if (!node || typeof window === "undefined") return;
    const rect = node.getBoundingClientRect();
    const alreadyVisible = rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
    if (alreadyVisible) {
      setIsVisible(true);
    }
  }, []);
  useEffect(() => {
    const node = ref.current;
    if (!node || once && isVisible) return;
    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold, isVisible]);
  return {
    ref,
    isVisible
  };
}

// --- Data ---

const processingLevels = [{
  id: "L1",
  title: "L1: Calibration",
  description: "Initial pre-processing of raw satellite imagery. We apply radiometric calibration to normalize sensor artifacts, making the data suitable for specialized professionals who run custom workflows.",
  icon: ImageIcon,
  iconColor: "text-primary",
  badge: null,
  delay: "delay-0"
}, {
  id: "L2",
  title: "L2: Orthorectification",
  description: "Imagery is corrected for terrain relief distortion and tied to a precise coordinate system. This is our most commonly used level, enabling pinpoint accurate measurements directly on the image.",
  icon: MapPin,
  iconColor: "text-[var(--color-chart-3)]",
  badge: "Most Popular",
  delay: "delay-100",
  highlightBorder: true
}, {
  id: "L3",
  title: "L3: Mosaics & 3D",
  description: "Transitioning to 3D spatial data. We stitch multiple L2 images together to create vast, seamless, and cloud-free Mosaics, providing uninterrupted visual coverage of massive territories.",
  icon: Layers,
  iconColor: "text-primary",
  badge: null,
  delay: "delay-200"
}];
const geoAIFeatures = [{
  title: "Automated Feature Extraction",
  description: "AI-driven identification of roads, buildings, and infrastructure to rapidly update maps and monitor urban expansion.",
  icon: ScanLine,
  iconColor: "text-cyan-500",
  delay: "delay-0"
}, {
  title: "Spectral Indices (NDVI, NDWI)",
  description: "Multispectral band analysis to monitor crop health, vegetation density, and water body dynamics with scientific precision.",
  icon: Leaf,
  iconColor: "text-emerald-500",
  delay: "delay-100"
}, {
  title: "Time-Series Change Detection",
  description: "Algorithmic comparison of temporal imagery to instantly highlight illegal construction, deforestation, or environmental degradation.",
  icon: History,
  iconColor: "text-cyan-500",
  delay: "delay-200"
}];

// --- Components ---

function TechnicalProcessingLevels() {
  const headerReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const gridReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return;
}
function GeoAIAnalytics() {
  const contentReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const imageReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return;
}

// --- Main Export ---

export default function ServicesPage() {
  return <div className="flex flex-col w-full">
      <TechnicalProcessingLevels />
      <GeoAIAnalytics />
    </div>;
}